/**
 * Health: the Argus cluster for admins (Track S, step S7; layout B of
 * `docs/design/sketches/argus-health-room.html`).
 *
 * On top, the cluster now: the scheduler and the data on the left, the three
 * queue lanes in the middle, the worker pools with their named workers on the
 * right. Under it, what the cluster did over a window: totals, worker time
 * spent on scheduled jobs against backtests, jobs per hour, a row per worker,
 * then failures and late ships.
 *
 * Read only. The state is asked for every 5 s, the statistics once a minute,
 * and neither while the tab is hidden. A worker with no heartbeat is shown
 * gone in words, never as healthy.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { getArgusHealth, getArgusHealthFinished, getArgusHealthStats } from '../../api/endpoints.ts';
import type { ArgusHealth, ArgusHealthFinished, ArgusHealthQueue, ArgusHealthStats, ArgusHealthWorker } from '../../api/types.ts';
import { userMessage } from '../../api/errors.ts';
import { shipLabel } from '../../utils/ships.ts';
import {
  LIVE_LATE_S, QUEUE_CHIPS, QUEUE_MORE, STATS_CHIPS, STATS_MORE, ago, backtestWindow, duration, problems, secondsLeft, windowWords,
} from './argus-health.ts';
import { type DistForm, renderDist, renderHourly, renderSplit, renderTiles, renderWorkerTable } from './argus-health-charts.ts';
import './argus-window-chips.ts';
import { rulesRoomOpenRule } from './argus-inbox-store.ts';
import { setArgusRoom } from './argus-lens.ts';

const LIVE_MS = 5_000;
const STATS_MS = 60_000;
/** Whether "What it did" is open: a convenience of this browser only. */
const FOLD_KEY = 'argus.health.didOpen';

/** How the distributions are drawn: also a convenience of this browser only. */
const FORM_KEY = 'argus.health.distForm';

function readOpen(): boolean {
  try {
    return localStorage.getItem(FOLD_KEY) !== '0';
  } catch {
    return true;
  }
}

function readForm(): DistForm {
  try {
    const v = localStorage.getItem(FORM_KEY);
    return v === 'box' || v === 'both' ? v : 'hist';
  } catch {
    return 'hist';
  }
}
function secondsSince(iso: string | null, now: string): number | null {
  return iso ? (Date.parse(now) - Date.parse(iso)) / 1000 : null;
}

function hhmm(iso: string): string {
  return new Date(iso).toISOString().slice(11, 16);
}

function day(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

function num(n: number): string {
  return n.toLocaleString('en-GB');
}

@customElement('argus-health-room')
export class ArgusHealthRoom extends LightElement {
  @state() private live: ArgusHealth | null = null;
  @state() private stats: ArgusHealthStats | null = null;
  @state() private finished: ArgusHealthFinished | null = null;
  @state() private hours = 24;
  /** The window of the queue cards' done and failed. */
  @state() private minutes = 15;
  @state() private liveFailed: string | null = null;
  @state() private statsFailed: string | null = null;
  /** The window `finished` was counted over: until a new answer comes, the old numbers stay, dimmed. */
  @state() private finishedMinutes = 0;
  @state() private didOpen = readOpen();
  @state() private distForm: DistForm = readForm();
  private timers: number[] = [];
  private askedStats = 0;
  private askedFinished = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadLive();
    void this.loadStats();
    void this.loadFinished();
    this.timers = [
      window.setInterval(() => !document.hidden && void this.loadLive(), LIVE_MS),
      window.setInterval(() => {
        if (document.hidden) return;
        void this.loadStats();
        void this.loadFinished();
      }, STATS_MS),
    ];
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.timers.forEach((t) => window.clearInterval(t));
  }

  private async loadLive(): Promise<void> {
    try {
      this.live = await getArgusHealth();
      this.liveFailed = null;
    } catch (error) {
      this.liveFailed = userMessage(error, 'The state of the cluster could not be read.');
    }
  }

  private async loadStats(): Promise<void> {
    const ask = ++this.askedStats;
    try {
      const answer = await getArgusHealthStats(this.hours);
      if (ask !== this.askedStats) return;
      this.stats = answer;
      this.statsFailed = null;
    } catch (error) {
      if (ask !== this.askedStats) return;
      this.statsFailed = userMessage(error, 'The statistics could not be read.');
    }
  }

  /** Done and failed per queue; a failure leaves the numbers as dashes, the chip says the rest. */
  private async loadFinished(): Promise<void> {
    const ask = ++this.askedFinished;
    try {
      const answer = await getArgusHealthFinished(this.minutes);
      if (ask !== this.askedFinished) return;
      this.finished = answer;
      this.finishedMinutes = this.minutes;
    } catch {
      if (ask === this.askedFinished) this.finished = null;
    }
  }

  private pickMinutes(minutes: number): void {
    if (minutes === this.minutes) return;
    // The old numbers stay, dimmed, until the new ones come: clearing them made the room jump.
    this.minutes = minutes;
    void this.loadFinished();
  }

  private toggleDid(): void {
    this.didOpen = !this.didOpen;
    try {
      localStorage.setItem(FOLD_KEY, this.didOpen ? '1' : '0');
    } catch {
      /* the fold opens again next time */
    }
  }

  private pickForm(form: DistForm): void {
    this.distForm = form;
    try {
      localStorage.setItem(FORM_KEY, form);
    } catch {
      /* the histogram comes back next time */
    }
  }

  /** One floating tip for every chart mark with `data-hl-tip`, moved by hand: no render per mouse move. */
  private onTipMove(e: PointerEvent): void {
    const tip = this.querySelector<HTMLElement>('.argus-hl-tip');
    if (!tip) return;
    const mark = (e.target as Element | null)?.closest?.('[data-hl-tip]');
    const text = mark?.getAttribute('data-hl-tip');
    if (!text) {
      tip.hidden = true;
      return;
    }
    tip.textContent = text;
    tip.hidden = false;
    const x = Math.min(window.innerWidth - tip.offsetWidth - 8, e.clientX + 14);
    const y = Math.min(window.innerHeight - tip.offsetHeight - 8, e.clientY + 14);
    tip.style.left = `${Math.max(8, x)}px`;
    tip.style.top = `${Math.max(8, y)}px`;
  }

  private hideTip(): void {
    const tip = this.querySelector<HTMLElement>('.argus-hl-tip');
    if (tip) tip.hidden = true;
  }

  private pickHours(hours: number): void {
    if (hours === this.hours) return;
    // The old statistics stay, dimmed, until the new ones come. Clearing them
    // shrank the page to one line, so the browser scrolled up and it flickered.
    this.hours = hours;
    void this.loadStats();
  }

  // ── the cluster now ───────────────────────────────────────────────────────

  /** One part of the stage: a tinted zone with its name, what it does, and an ⓘ. */
  private zone(kind: 'plan' | 'wait' | 'run', title: string, verb: string, tip: string, body: unknown, tools: unknown = nothing): TemplateResult {
    return html`<section class="argus-hl-zone is-${kind}" aria-label=${title}>
      <div class="argus-hl-zone-h">
        <b>${title}</b><span>${verb}</span>
        <span class="argus-hl-info" tabindex="0" role="note" aria-label=${tip} data-tip=${tip}>i</span>
        ${tools}
      </div>
      ${body}
    </section>`;
  }

  private arrow(): TemplateResult {
    return html`<div class="argus-hl-arrow" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M4 12h15M14 7l5 5-5 5"></path></svg>
    </div>`;
  }

  /** A rule on a chip of one size; a click opens it in the Rules room. A question has no rule to open. */
  private chip(rule: { rule_id: string | null; name: string | null }, meta?: string, tip?: string): TemplateResult {
    const name = rule.name ?? 'a rule';
    const body = html`<span class="argus-hl-chip-n">${name}</span>${meta ? html`<span class="argus-hl-chip-m">${meta}</span>` : nothing}`;
    const id = rule.rule_id;
    if (!id) return html`<span class="argus-hl-chip" title=${tip ?? name}>${body}</span>`;
    return html`<button type="button" class="argus-hl-chip is-link" title=${tip ?? `Open ${name}`} @click=${() => this.openRule(id)}>
      ${body}<span class="argus-hl-chip-go" aria-hidden="true">↗</span>
    </button>`;
  }

  private more(n: number, words = ''): TemplateResult | typeof nothing {
    return n > 0 ? html`<span class="argus-hl-chip is-more">+${num(n)}${words}</span>` : nothing;
  }

  private openRule(id: string): void {
    rulesRoomOpenRule.set(id);
    setArgusRoom('rules');
  }

  private renderSchedulers(h: ArgusHealth): TemplateResult {
    const leader = h.schedulers.find((s) => s.silent_s <= h.alive_seconds);
    const next = h.upcoming[0];
    return html`<div class="argus-hl-side">
      ${leader
        ? html`<div class="argus-hl-sched">
            <div class="argus-hl-box is-lead" tabindex="0" aria-describedby="argus-hl-pop">
              <b>Scheduler · leading</b>
              <span>last tick ${ago(leader.silent_s)} ago</span>
              <span class="argus-hl-next">
                <span>next run</span>
                <b>${next ? `${hhmm(next.at)} · in ${ago(secondsSince(h.now, next.at))}` : 'none in the next hour'}</b>
              </span>
              <span class="argus-lfaint">hover for the next hour</span>
            </div>
            ${this.renderSchedulerCard(h, leader)}
          </div>`
        : html`<div class="argus-hl-box is-bad">
            <b>No scheduler reports</b>
            <span>${h.schedulers[0] ? `last seen ${ago(h.schedulers[0].silent_s)} ago` : 'none in the last hour'}</span>
            <span>Nothing new is planned.</span>
          </div>`}
      <div class="argus-hl-sublabel">Data</div>
      ${h.data.length
        ? h.data.map(
            (d) => html`<div class="argus-hl-box ${d.late.length ? 'is-warn' : ''}">
              <b>${d.source_key === 'historical' ? 'S3 data' : d.source_key}</b>
              <span>${d.entities} ships · newest through ${day(d.newest)}</span>
              ${d.late.length
                ? html`<span class="argus-hl-warn">${d.late.length} ship${d.late.length > 1 ? 's' : ''} a day or more behind</span>`
                : html`<span>none late</span>`}
            </div>`,
          )
        : html`<div class="argus-hl-box"><b>Data</b><span>No watermarks yet.</span></div>`}
      <div class="argus-hl-box"><b>Live data</b><span class="argus-lfaint">no Live rules yet</span></div>
    </div>`;
  }

  /** On hover or focus of the scheduler: its last tick, the next hour, what waits for data. */
  private renderSchedulerCard(h: ArgusHealth, leader: ArgusHealth['schedulers'][number]): TemplateResult {
    const t = leader.last_tick;
    const nowMs = Date.parse(h.now);
    const pct = (iso: string): number => Math.min(100, Math.max(0, ((Date.parse(iso) - nowMs) / 3_600_000) * 100));
    const most = Math.max(1, ...h.upcoming.map((u) => u.rules.length));
    return html`<div class="argus-hl-pop" id="argus-hl-pop" role="tooltip">
      <div>
        <h5>Last tick${t ? ` · ${hhmm(t.at)} UTC` : ''}</h5>
        ${t
          ? html`<dl class="argus-hl-facts">
              <dt>planned</dt><dd>${t.jobs ? `${num(t.jobs)} job${t.jobs === 1 ? '' : 's'} · ${num(t.items)} rule runs` : 'nothing due'}</dd>
              <dt>skipped</dt><dd>${t.skipped ? `${num(t.skipped)} for no new data` : 'none'}</dd>
              <dt>took</dt><dd>${t.took_s} s · ${num(t.rules)} rules read</dd>
            </dl>`
          : html`<p class="argus-lfaint">Not reported yet: this scheduler is older than migration 0051.</p>`}
      </div>
      <div>
        <h5>Next hour · a bigger dot is more rules</h5>
        ${h.upcoming.length
          ? html`<div class="argus-hl-hour" role="img" aria-label=${`${h.upcoming.length} runs in the next hour`}>
              <span class="argus-hl-hour-axis"></span><span class="argus-hl-hour-now"></span>
              ${h.upcoming.map((u) => {
                const size = 0.4 + (0.6 * u.rules.length) / most;
                return html`<span class="argus-hl-hour-dot" style=${`left:${pct(u.at)}%;width:${size}rem;height:${size}rem`}
                  title=${`${hhmm(u.at)}: ${u.rules.length} rules`}></span>
                  <span class="argus-hl-hour-n" style=${`left:${pct(u.at)}%`}>${u.rules.length}</span>`;
              })}
              ${[0, 15, 30, 45, 60].map((m) => html`<span class="argus-hl-hour-tick" style=${`left:${(m / 60) * 100}%`}>${hhmm(new Date(nowMs + m * 60_000).toISOString())}</span>`)}
            </div>`
          : html`<p class="argus-lfaint">No deployed rule runs in the next hour.</p>`}
      </div>
      ${h.upcoming.length
        ? html`<div>
            <h5>Up next</h5>
            ${h.upcoming.slice(0, 4).map((u, i) => html`<div class="argus-hl-up ${i === 0 ? 'is-first' : ''}">
              <b>${hhmm(u.at)}</b><span class="argus-lfaint">in ${ago(secondsSince(h.now, u.at))}</span>
              <span>${u.rules.length} rule${u.rules.length === 1 ? '' : 's'}</span>
              <div class="argus-hl-chips is-three">${u.rules.slice(0, 2).map((r) => this.chip(r))}${this.more(u.rules.length - 2)}</div>
            </div>`)}
          </div>`
        : nothing}
      ${t
        ? html`<div>
            <h5>Waiting for data</h5>
            ${t.waiting.length
              ? html`<div class="argus-hl-chips">
                  ${t.waiting.map((w) => this.chip(w, `${num(w.entities)} ship${w.entities === 1 ? '' : 's'}`,
                    `${w.name ?? 'a rule'} · due since ${hhmm(w.since)} UTC · ${w.entities} ships have no new data`))}
                  ${this.more(t.waiting_rules - t.waiting.length, ' rules')}
                </div>`
              : html`<p class="argus-lfaint">Nothing waits for data.</p>`}
          </div>`
        : nothing}
      <div class="argus-hl-pop-foot">
        A standby waits without reporting · ${leader.watcher_id.replace(/^scheduler@/, '').split(':')[0]}
        · version ${leader.version ?? '?'}${leader.started_at ? ` · up ${ago(secondsSince(leader.started_at, h.now))}` : ''}
      </div>
    </div>`;
  }

  private renderNumbers(waiting: number, running: number, done: number | null, failed: number | null): TemplateResult {
    const cell = (v: number | null, word: string, tone = ''): TemplateResult =>
      html`<div class="argus-hl-num ${v ? tone : 'is-zero'}"><b>${v === null ? '-' : num(v)}</b><span>${word}</span></div>`;
    const words = windowWords(this.minutes);
    return html`<div class="argus-hl-nums">
      <div class="argus-hl-ngroup">
        <span class="argus-hl-ncap">now</span>
        <div class="argus-hl-ncells">${cell(waiting, 'waiting')}${cell(running, 'running')}</div>
      </div>
      <div class="argus-hl-ngroup is-win ${this.finished && this.finishedMinutes !== this.minutes ? 'is-stale' : ''}">
        <span class="argus-hl-ncap">last ${words}</span>
        <div class="argus-hl-ncells">${cell(done, 'done', 'is-ok')}${cell(failed, 'failed', 'is-bad')}</div>
      </div>
    </div>`;
  }

  private renderLane(q: ArgusHealthQueue, h: ArgusHealth): TemplateResult {
    // Backtests run whole through `backtest_request` until S5 cuts them into jobs.
    const bt = q.name === 'backtest' ? h.backtests : null;
    const waiting = q.waiting + (bt?.waiting ?? 0);
    const running = q.running + (bt?.running ?? 0);
    const oldest = Math.max(q.oldest_wait_s, bt?.oldest_wait_s ?? 0);
    const late = q.name === 'live' && oldest > LIVE_LATE_S;
    const pool = h.pools.find((p) => p.queues.includes(q.name));
    const done = this.finished?.queues.find((f) => f.name === q.name);
    const asked = bt?.asked ?? [];
    const named = q.rules.length + asked.length;
    return html`<div class="argus-hl-lane ${late ? 'is-warn' : ''}">
      <div class="argus-hl-lane-h">
        <span><b>${q.name}</b>${pool ? html` <span class="argus-hl-for">→ ${pool.name} pool</span>` : nothing}</span>
        <span class=${waiting ? 'argus-hl-warn' : ''}>${waiting ? `oldest waits ${ago(oldest)}` : q.max_running ? `at most ${q.max_running} at once` : ''}</span>
      </div>
      ${named
        ? html`<div class="argus-hl-chips">
            ${q.rules.map((r) => this.chip(r, `${num(r.entities)} ship${r.entities === 1 ? '' : 's'}`))}
            ${this.more(q.rules_waiting - q.rules.length, ' rules')}
            ${asked.map((b) => this.chip(b, b.asked_by ?? undefined,
              `${b.name} · ${backtestWindow(b)} · asked by ${b.asked_by ?? 'someone'} ${ago(secondsSince(b.requested_at, h.now))} ago`))}
            ${this.more((bt?.waiting ?? 0) - asked.length, ' asked')}
          </div>`
        : html`<span class="argus-lfaint">Nothing waits${running ? ` · ${running} running on the workers →` : ''}</span>`}
      ${this.renderNumbers(waiting, running, done ? done.done : null, done ? done.failed : null)}
    </div>`;
  }

  private renderWorker(w: ArgusHealthWorker, now: string): TemplateResult {
    const busy = w.alive && (w.job_id !== null || w.backtest !== null);
    let doing: TemplateResult;
    if (!w.alive) doing = html`<span class="argus-hl-bad">no heartbeat for ${ago(w.beat_age_s)}</span>`;
    else if (w.job_id !== null) {
      const names = w.job_rule_names ?? [];
      const rules = w.job_rules ?? names.length;
      doing = html`<span title=${w.job_entity ? `on ${shipLabel(w.job_entity)}` : ''}>${w.job_queue} · ${rules} rule${rules === 1 ? '' : 's'} · ${ago(secondsSince(w.job_started_at, now))}</span>
        <div class="argus-hl-chips is-one">${names.slice(0, 1).map((r) => this.chip(r))}${this.more(rules - 1)}</div>`;
    } else if (w.backtest) {
      const b = w.backtest;
      const total = b.total_entities;
      const share = total ? Math.min(100, Math.round((100 * b.done_entities) / total)) : 0;
      const left = secondsLeft(b.done_entities, total, b.claimed_at, now);
      doing = html`<span>backtest · ${share}%</span>
        <div class="argus-hl-chips">${this.chip(b, undefined, `${b.name} · ${backtestWindow(b)} · asked by ${b.asked_by ?? 'someone'}`)}</div>
        <span class="argus-hl-progress" role="img" aria-label=${`${b.done_entities} of ${total ?? '?'} ships`}><i style=${`width:${share}%`}></i></span>
        <span class="argus-hl-btmeta">${b.done_entities} of ${total ?? '?'} ships · ${backtestWindow(b)}${left !== null ? ` · ${ago(left)} left` : ''}</span>
        ${b.asked_by ? html`<span class="argus-hl-btmeta">asked by ${b.asked_by}</span>` : nothing}`;
    } else doing = html`<span class="argus-lfaint">idle</span>`;
    return html`<div class="argus-hl-worker ${busy ? 'is-busy' : ''} ${w.alive ? '' : 'is-gone'}"
      title=${`${w.task ?? ''} · ${w.version ?? ''} · up ${ago(secondsSince(w.started_at, now))}`}>
      <div class="argus-hl-worker-top"><b>${w.name}</b><span>beat ${ago(w.beat_age_s)} ago</span></div>
      <div class="argus-hl-doing">${doing}</div>
      <div class="argus-hl-use">
        <span><span class="argus-hl-meter"><i style=${`width:${Math.min(100, w.cpu_percent ?? 0)}%`}></i></span>${w.cpu_percent === null ? '-' : `${Math.round(w.cpu_percent)}%`}</span>
        <span>${w.memory_mb === null ? '-' : `${Math.round(w.memory_mb)} MB`}</span>
      </div>
    </div>`;
  }

  private renderPools(h: ArgusHealth): TemplateResult {
    return html`<div class="argus-hl-pools">
      ${h.pools.map((p) => {
        const alive = p.workers.filter((w) => w.alive).length;
        const free = Math.max(0, p.max - p.workers.length);
        return html`<div class="argus-hl-pool">
          <div class="argus-hl-pool-h"><b>${p.name} pool</b><span>takes ${p.queues.join(' + ')} · ${alive} running, up to ${p.max}</span></div>
          <div class="argus-hl-workers">
            ${p.workers.map((w) => this.renderWorker(w, h.now))}
            ${Array.from({ length: free }, () => html`<div class="argus-hl-worker is-slot">free slot<small>starts when jobs wait</small></div>`)}
          </div>
        </div>`;
      })}
    </div>`;
  }

  // ── what it did ───────────────────────────────────────────────────────────

  private renderFailures(h: ArgusHealth): TemplateResult {
    return html`<div class="argus-hl-card">
      <div class="argus-hl-card-h"><h4>Failed, last 24 h</h4><span>${h.failures.length ? `${h.failures.length}${h.failures.length === 50 ? '+' : ''}` : 'none'}</span></div>
      ${h.failures.length
        ? html`<div class="argus-ltable-wrap"><table class="argus-ltable argus-hl-table">
            <thead><tr><th class="argus-hl-col-when">When</th><th>What</th><th class="argus-hl-col-who">Who</th><th>Why</th></tr></thead>
            <tbody>${h.failures.map((f) => html`<tr>
              <td class="is-mono">${hhmm(f.at)}</td>
              <td>${f.kind === 'backtest' ? 'a backtest' : html`${f.entity ? shipLabel(f.entity) : ''} · ${f.kind === 'rule' ? f.rule_name ?? 'a rule' : `${f.rules ?? 0} rules`}`}</td>
              <td>${f.worker ?? '-'}</td>
              <td>${f.error ?? 'no reason given'}${f.attempts && f.attempts > 1 ? html` <span class="argus-lfaint">· attempt ${f.attempts}</span>` : nothing}${f.error_ref ? html` <span class="is-mono argus-lfaint">· ${f.error_ref}</span>` : nothing}</td>
            </tr>`)}</tbody></table></div>`
        : html`<p class="argus-lfaint">Nothing failed in the last 24 hours.</p>`}
    </div>`;
  }

  private renderLate(h: ArgusHealth): TemplateResult {
    const late = h.data.flatMap((d) => d.late.map((x) => ({ ...x, source: d.source_key, newest: d.newest })));
    return html`<div class="argus-hl-card">
      <div class="argus-hl-card-h"><h4>Late data</h4><span>a day or more behind the newest ship</span></div>
      ${late.length
        ? html`<div class="argus-ltable-wrap"><table class="argus-ltable argus-hl-table">
            <thead><tr><th>Ship</th><th>Source</th><th>Data through</th><th class="is-n">Behind</th></tr></thead>
            <tbody>${late.map((x) => html`<tr>
              <td>${shipLabel(x.entity)}</td><td>${x.source === 'historical' ? 'S3' : x.source}</td>
              <td class="is-mono">${day(x.data_through)}</td>
              <td class="is-n">${ago((Date.parse(x.newest) - Date.parse(x.data_through)) / 1000)}</td>
            </tr>`)}</tbody></table></div>`
        : html`<p class="argus-lfaint">Every ship is up to date with the others.</p>`}
    </div>`;
  }

  /** In the folded heading: the window and its main numbers, so a fold still says something. */
  private didSummary(s: ArgusHealthStats | null): string {
    // The numbers' own window: while a new one loads, the heading still matches them.
    const label = windowWords((s?.hours ?? this.hours) * 60);
    if (!s) return `last ${label}`;
    const t = s.totals;
    return [
      `last ${label}`,
      `${num(t.scheduled_done)} jobs`,
      t.scheduled_failed ? `${num(t.scheduled_failed)} failed` : 'none failed',
      t.run_p95_s === null ? '' : `job p95 ${duration(t.run_p95_s)}`,
      t.wait_p95_s === null ? '' : `wait p95 ${duration(t.wait_p95_s)}`,
      `${num(t.backtests_done)} backtests`,
    ].filter(Boolean).join(' · ');
  }

  /** What it did: folds like a zone of the rule editor; the window stays in its heading. */
  private renderDid(s: ArgusHealthStats | null): TemplateResult {
    const open = this.didOpen;
    const stale = s !== null && s.hours !== this.hours && !this.statsFailed;
    return html`<section class="argus-e-zone is-fold argus-hl-did ${open ? 'is-open' : ''}" aria-label="What it did">
      <div class="argus-e-zone-h">
        <button type="button" class="argus-e-zone-toggle" aria-expanded=${open ? 'true' : 'false'} @click=${() => this.toggleDid()}>
          <span class="argus-e-zone-caret" aria-hidden="true"></span>
          <span class="argus-e-zone-name">What it did</span>
          <span class="argus-e-zone-sum ${s ? 'is-done' : ''}">${this.didSummary(s)}</span>
        </button>
        ${open && stale ? html`<span class="spinner spinner-sm argus-hl-did-wait" role="status" aria-label="Counting the new window"></span>` : nothing}
        ${open
          ? html`<argus-window-chips class="argus-hl-did-win" label="Window" .chips=${STATS_CHIPS} .more=${STATS_MORE}
              .value=${this.hours} @window-pick=${(e: CustomEvent<number>) => this.pickHours(e.detail)}></argus-window-chips>`
          : nothing}
      </div>
      <div class="argus-e-zone-b argus-hl-did-b ${stale ? 'is-stale' : ''}" ?hidden=${!open} aria-busy=${stale ? 'true' : 'false'}
        @pointermove=${(e: PointerEvent) => this.onTipMove(e)} @pointerleave=${() => this.hideTip()}>
        ${this.statsFailed ? html`<p class="argus-hl-bad">${this.statsFailed}</p>` : nothing}
        ${s
          ? html`${renderTiles(s, windowWords(s.hours * 60))}
              ${s.dist
                ? html`<div class="argus-hl-two is-even">
                    ${renderDist('Job time', 'started to finished: one job is one ship and all its rules', s.dist.run, this.distForm, (f) => this.pickForm(f))}
                    ${renderDist('Wait time', 'queued to claimed by a worker', s.dist.wait, this.distForm, (f) => this.pickForm(f))}
                  </div>`
                : nothing}
              <div class="argus-hl-two is-wide">${renderHourly(s)}${renderSplit(s)}</div>
              ${renderWorkerTable(s)}`
          : html`<p class="argus-lfaint"><span class="spinner spinner-sm" aria-hidden="true"></span> Counting…</p>`}
        <div class="argus-hl-tip" role="tooltip" hidden></div>
      </div>
    </section>`;
  }

  override render(): TemplateResult {
    const h = this.live;
    if (!h) {
      return html`<div class="argus-room argus-hl">
        <h2>Health</h2>
        ${this.liveFailed
          ? html`<p class="argus-bt-note is-error">${this.liveFailed}</p>`
          : html`<p class="argus-lfaint"><span class="spinner spinner-sm" aria-hidden="true"></span> Reading the cluster…</p>`}
      </div>`;
    }
    const issues = problems(h);
    const s = this.stats;
    return html`<div class="argus-room argus-hl">
      <div class="argus-room-head argus-hl-head">
        <h2>Health <span class="admin-role-chip argus-admin-chip">Admin</span></h2>
        <span class="argus-lfaint">${this.liveFailed ? html`<span class="argus-hl-bad">${this.liveFailed}</span>` : `as of ${new Date(h.now).toISOString().slice(11, 19)} UTC`}</span>
        <span class="argus-chip argus-hl-status ${issues.length ? 'is-watch' : 'is-ok'}">${issues.length ? issues.join(' · ') : 'All well'}</span>
      </div>
      <div class="argus-hl-stage">
        ${this.zone('plan', 'Scheduler', 'plans the jobs',
          'Each minute the scheduler reads the deployed rules, finds what is due on which ship, and puts one job per ship and tick in a queue. One scheduler leads; a second waits to take over. Data says how far each ship\'s readings reach.',
          this.renderSchedulers(h))}
        ${this.arrow()}
        ${this.zone('wait', 'Queues', 'jobs wait here',
          'A queue is a waiting line in the database. A job stays in its queue until a free worker of its pool takes it, oldest first; then it shows on that worker. A chip is a rule that waits, with how many ships it still has to run on. live lets at most 4 run at once, so the Live API is not flooded.',
          html`<div class="argus-hl-lanes">${h.queues.map((q) => this.renderLane(q, h))}</div>`,
          html`<argus-window-chips class="argus-hl-window" label="Window for done and failed" .chips=${QUEUE_CHIPS} .more=${QUEUE_MORE}
            .value=${this.minutes} @window-pick=${(e: CustomEvent<number>) => this.pickMinutes(e.detail)}></argus-window-chips>`)}
        ${this.arrow()}
        ${this.zone('run', 'Workers', 'run the jobs',
          'Each worker is one ECS task, a small machine of its own, named when it starts. It reads a ship once and checks every rule of the job. A pool adds a worker when a job waits 2 minutes, up to 4, and stops one after 10 quiet minutes.',
          this.renderPools(h))}
      </div>
      <div class="argus-hl-legend">
        <span><i></i>a rule that waits · click to open it</span>
        <span><i class="is-busy"></i>worker busy, with the rules it runs</span>
        <span><i class="is-slot"></i>free slot: no task yet, autoscaling starts one when jobs wait</span>
      </div>

      ${this.renderDid(s)}
      <div class="argus-hl-two">${this.renderFailures(h)}${this.renderLate(h)}</div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-health-room': ArgusHealthRoom;
  }
}
